import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import PersistentMFELoader from './PersistentMFELoader';
import { createLogger } from '../utils/logger';
import styles from './Layout.module.css';

const logger = createLogger('Shell::Layout');

/**
 * Syncs browser URL changes (pushState/popstate from MFEs) back to React Router.
 * Without this, useLocation() in shell components stays stale when MFEs
 * navigate internally via window.history.pushState + popstate events.
 */
/**
 * Syncs React Router with browser URL changes from MFEs.
 * MFEs navigate via pushState/replaceState — this intercepts those calls
 * so useLocation() updates across all shell components (breadcrumbs,
 * PersistentMFELoader, etc.).
 */
const useRouteSync = () => {
    const navigate = useNavigate();

    useEffect(() => {
        let syncing = false;

        const syncRoute = () => {
            if (syncing) return;
            syncing = true;
            const path = window.location.pathname + window.location.search;
            logger.info('Route sync', { path });
            navigate(path, { replace: true });
            syncing = false;
        };

        // Intercept pushState from MFEs (not replaceState — avoid loop with navigate)
        const originalPushState = window.history.pushState.bind(window.history);

        window.history.pushState = (...args) => {
            originalPushState(...args);
            syncRoute();
        };

        // Browser back/forward
        window.addEventListener('popstate', syncRoute);

        return () => {
            window.history.pushState = originalPushState;
            window.removeEventListener('popstate', syncRoute);
        };
    }, [navigate]);
};

function Layout({ children, onNavigate }) {
    useRouteSync();

    return (
        <div className={styles.shell}>
            <Navbar onNavigate={onNavigate} />
            <div className={styles.content}>
                {/* Main MFE content — grows to fill available space */}
                <main className={styles.main}>
                    {children}
                </main>

                {/* Bottom panels — in document flow (e.g., Reports) */}
                <PersistentMFELoader.Bottom />
            </div>

            {/* Overlay panels — fixed position, outside document flow (e.g., ShopFloor AI).
                Layout/stacking rules live in `.overlayMount` (Layout.module.css). */}
            <div className={styles.overlayMount}>
                <PersistentMFELoader.Overlay />
            </div>
        </div>
    );
}

export default Layout;